import { reactive } from "vue";

// Subscription statuses that grant a paid plan. Mirrors the backend's
// stripeActiveStatuses (iapApi.py). Anything else (e.g. "canceled") is free.
const ACTIVE_BILLING_STATUSES = ["active", "trialing", "past_due"];

// Plan display names are i18n KEYS, not English literals: this store is plain
// JS with no access to the i18n instance, and every consumer already renders
// inside a component that can call $t. Keeping the English here shipped
// "Foundation Plan" untranslated to all 30 non-English bundles.
// The prices below are the STRIPE prices, in USD. They are not what the App
// Store or Google Play charges: the store prices are higher on purpose, to
// absorb the platform commission, and they are not a uniform markup because
// Apple's and Google's price points are discrete tiers. context/pricing.md
// holds the full table and the rule that follows from it — a surface must
// display the price of the rail that surface will actually use — so do not
// render a figure from here on a surface the store is going to charge.
export const pricingStore = reactive({
  plans: {
    free: {
      nameKey: "pricing.planNames.free",
      // Must equal FREE_USER_FEATURES in functions/source/helpers/users.py and
      // functions/shared/core_common/users.py. A client ceiling ABOVE the
      // server's lets the user start a chat the server deletes and raises on,
      // which has no client handler. tests/unit/freebiesEntitlement.test.js
      // pins all three copies together.
      lifetimeChats: 3,
      lifetimeLessons: 1,
      price: 0,
      lookupKey: null,
    },
    foundation: {
      nameKey: "pricing.planNames.foundation",
      chatsPerMonth: 8,
      lessonsPerMonth: 4,
      monthlyPrice: 5,
      yearlyPrice: 4.17,
      yearlyTotal: 50,
      yearlyMonthsFree: 2,
      monthlyLookupKey: "foundation-month",
      yearlyLookupKey: "foundation-year",
      iapBasePlans: { monthly: "foundation-monthly", yearly: "foundation-yearly" },
    },
    fluency: {
      nameKey: "pricing.planNames.fluency",
      chatsPerMonth: 20,
      lessonsPerMonth: 12,
      multiLingual: true,
      monthlyPrice: 10,
      yearlyPrice: 8.33,
      yearlyTotal: 100,
      yearlyMonthsFree: 2,
      monthlyLookupKey: "fluency-month",
      yearlyLookupKey: "fluency-year",
      iapBasePlans: { monthly: "fluency-monthly", yearly: "fluency-yearly" },
    },
  },
  // Single Google Play subscription product; the base plan encodes the tier.
  iapProductId: "articulearn_premium",
  getPlanByLookupKey(lookupKey) {
    for (const [planId, plan] of Object.entries(this.plans)) {
      if (
        plan.monthlyLookupKey === lookupKey ||
        plan.yearlyLookupKey === lookupKey
      ) {
        return { ...plan, id: planId };
      }
    }
    return { ...this.plans.free, id: "free" };
  },
  // Resolve the plan a user is currently entitled to. A canceled subscription
  // keeps its lookupKey, so the status MUST be checked or consumers would read
  // the old paid plan. Mirrors the backend's stripeActiveStatuses (iapApi.py).
  // This is the single source of truth for "what plan is this user on" — read
  // it instead of calling getPlanByLookupKey(billing.lookupKey) directly.
  getCurrentPlan(billing) {
    if (!billing || !ACTIVE_BILLING_STATUSES.includes(billing.status)) {
      return { ...this.plans.free, id: "free" };
    }
    return this.getPlanByLookupKey(billing.lookupKey);
  },
  getPlanDetailsById(planId) {
    return { ...(this.plans[planId] || this.plans.free), id: planId || "free" };
  },
  getBasePlanId(planId, billingCycle) {
    const plan = this.plans[planId];
    if (!plan || !plan.iapBasePlans) return null;
    return billingCycle === "yearly"
      ? plan.iapBasePlans.yearly
      : plan.iapBasePlans.monthly;
  },
  getPlanByBasePlanId(basePlanId) {
    for (const [planId, plan] of Object.entries(this.plans)) {
      if (
        plan.iapBasePlans &&
        (plan.iapBasePlans.monthly === basePlanId ||
          plan.iapBasePlans.yearly === basePlanId)
      ) {
        return { ...plan, id: planId };
      }
    }
    return { ...this.plans.free, id: "free" };
  },
});
